'use client';

import { useModal } from '@/hooks/useModal';
import type { AdultContentBlockedStatus } from '@/lib/adultContent';
import { createIdentityVerificationStartUrl } from '@/lib/identityVerificationLauncher';
import webViewService from '@/lib/webViewService';
import { Column } from '@vuddy/components';
import { getClientSideUserAgent } from '@vuddy/utils';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef } from 'react';

interface AdultSaleAccessGateProps {
  dismissToHome?: boolean;
  returnUrl: string;
  status: AdultContentBlockedStatus;
}

const hasHistoryToGoBack = () =>
  typeof window !== 'undefined' &&
  (window.history.state?.historyIndex ?? 0) > 0;

export const AdultSaleAccessGate = ({
  dismissToHome = false,
  returnUrl,
  status,
}: AdultSaleAccessGateProps) => {
  const router = useRouter();
  const { confirm } = useModal();
  const openedRef = useRef(false);
  const isAppWebView = useMemo(
    () => webViewService.isAppWebView(getClientSideUserAgent()),
    []
  );

  const replaceWithAppAwareTarget = useCallback(
    (target: string) => {
      if (isAppWebView) {
        if (target === '/') {
          webViewService.close();
          return;
        }

        router.replace(target);
        return;
      }

      router.replace(target);
    },
    [isAppWebView, router]
  );

  const moveBackOrFallback = useCallback(() => {
    if (dismissToHome) {
      replaceWithAppAwareTarget('/');
      return;
    }

    if (hasHistoryToGoBack()) {
      router.back();
      return;
    }

    replaceWithAppAwareTarget('/');
  }, [dismissToHome, replaceWithAppAwareTarget, router]);

  const goToIdentityVerification = useCallback(() => {
    router.push(createIdentityVerificationStartUrl({ returnUrl }));
  }, [returnUrl, router]);

  useEffect(() => {
    if (openedRef.current) return;
    openedRef.current = true;

    if (status === 'SELF_AUTH_REQUIRED') {
      confirm({
        title: '안내',
        description: (
          <>
            연령 확인이 필요한 내용이 포함되어 있습니다.
            <br />
            본인 인증을 진행하시겠습니까?
          </>
        ),
        confirmText: '확인',
        cancelText: '취소',
        hasCancel: true,
        onCancel: moveBackOrFallback,
        onConfirm: goToIdentityVerification,
      });
      return;
    }

    if (status === 'AGE_RESTRICTED') {
      confirm({
        title: '안내',
        description: '이용 가능한 연령 기준을 충족하지 않아 접근이 제한됩니다.',
        confirmText: '확인',
        hasCancel: false,
        onConfirm: moveBackOrFallback,
      });
      return;
    }

    confirm({
      title: '일시적인 문제가 발생했습니다.',
      description: (
        <>
          잠시 후 다시 시도해주세요.
          <br />
          문제가 계속 발생할 경우 고객센터로 문의해주세요.
        </>
      ),
      confirmText: '확인',
      hasCancel: false,
      onConfirm: moveBackOrFallback,
    });
  }, [confirm, goToIdentityVerification, moveBackOrFallback, status]);

  return <Column minHeight="100vh" data-testid="adult-sale-access-gate" />;
};
